import { z } from "zod"

/**
 * Typed environment variables for Vite-based tint-admin application
 * All variables must be prefixed with VITE_ to be exposed to the client
 */

const VERSIONED_VIEWER3D_WIDGET_PATH =
  /^\/3d-viewer\/v?[0-9]+(?:\.[0-9]+){0,3}(?:-[0-9A-Za-z][0-9A-Za-z._-]*)?\/widget\.js$/

const isVersionPinnedViewer3DWidgetUrl = (value: string | undefined): boolean => {
  if (!value) return false

  try {
    const url = new URL(value)
    return (
      url.protocol === "https:" &&
      url.origin === "https://tintvto.com" &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash &&
      VERSIONED_VIEWER3D_WIDGET_PATH.test(url.pathname)
    )
  } catch {
    return false
  }
}

const BaseEnvSchema = z.object({
  // Vite mode. `devstand` is the DEPLOYED dev stand (app-dev): real Clerk
  // login, CDN viewer widget, Sentry on — local-only affordances (like the
  // admin auth bypass) are hard-gated to `development` and can never apply.
  MODE: z.enum(["development", "devstand", "production", "test"]),

  // API Endpoints
  VITE_PLATFORM_BILLING_TINTVTO_SERVICE_ENDPOINT: z.string().trim().min(1),
  VITE_PLATFORM_ADMIN_TINTVTO_SERVICE_ENDPOINT: z.string().trim().min(1),
  // The extracted 3D admin service (admin decomposition, release A). Serves
  // the same /api/v{1,2}/admin path shape as admin on its own gateway host.
  VITE_PLATFORM_VIEWER3D_ADMIN_TINTVTO_SERVICE_ENDPOINT: z.string().trim().min(1),

  // CDN Configuration
  VITE_CDN_ENDPOINT: z.string(),
  VITE_CDN_UPLOAD_ENDPOINT: z.string(),
  VITE_TINT_MERCHANT_CDN: z.string(),

  // Widget & SDK
  VITE_TINT_WIDGET_LINK: z.string(),
  VITE_SDK_TOKEN: z.string(),
  // Internal Public Try-On API metadata. Never render it in merchant-facing
  // snippets; the widget bundle owns its stage-specific API endpoint.
  VITE_PLATFORM_PUBLIC_API_URL: z.string().optional(),

  // Home page "Watch the setup tutorial" card target. The card is always
  // rendered (product decision 2026-08-02); until the real video ships this
  // stays a placeholder link, swappable per environment without a release.
  VITE_HOME_TUTORIAL_VIDEO_URL: z.string().trim().url().optional(),

  // 3D Viewer
  VITE_VIEWER3D_WIDGET_URL: z.string().trim().url().optional(),
  // Platform-hosted shopper demo stand ({env}.tintvto.com/3d-viewer). The
  // integration page links here (with a freshly minted key) so merchants can
  // preview the viewer before touching their store.
  VITE_VIEWER3D_DEMO_URL: z.string().trim().url().optional(),
  // Public viewer API the demo stand should call. Only needed when the demo
  // stand is a LOCAL stand (localhost:5174) that can't resolve its api-base from
  // a CDN config.json — the integration page then appends it as ?api-base=… so
  // the local widget validates the key against the LOCAL viewer service (3009).
  VITE_VIEWER3D_DEMO_API_BASE: z.string().trim().url().optional(),

  // Local QA Auth
  VITE_LOCAL_ADMIN_AUTH_BYPASS: z.enum(["true", "false"]).optional(),
  VITE_LOCAL_ADMIN_EMAIL: z.string().optional(),
  VITE_LOCAL_ADMIN_NAME: z.string().optional(),
  VITE_LOCAL_ADMIN_USER_ID: z.string().optional(),
  VITE_LOCAL_PLATFORM_MERCHANT_ID: z.string().optional(),

  // Clerk Auth
  VITE_CLERK_PUBLISHABLE_KEY: z.string(),
  VITE_CLERK_SIGN_IN_URL: z.string().url(),
  VITE_CLERK_SIGN_UP_URL: z.string().url(),
  VITE_CLERK_SIGN_IN_FORCE_REDIRECT_URL: z.string().optional(),
  VITE_CLERK_SIGN_UP_FORCE_REDIRECT_URL: z.string().optional(),
  VITE_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL: z.string().optional(),
  VITE_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL: z.string().optional(),

  // Sentry
  VITE_SENTRY_ENV: z.string().optional(),

  // PostHog
  VITE_POSTHOG_KEY: z.string().optional(),
  VITE_POSTHOG_HOST: z.string().url().optional(),
  VITE_POSTHOG_ENABLED: z.enum(["true", "false"]).optional(),
  VITE_POSTHOG_SESSION_RECORDING_ENABLED: z.enum(["true", "false"]).optional(),

  // Billing: portal-return entitlement poll cadence (BillingGate). Optional
  // integer-like strings; the gate falls back to 2s × 30 attempts.
  VITE_BILLING_PORTAL_RETURN_POLL_INTERVAL_MS: z.string().regex(/^\d+$/).optional(),
  VITE_BILLING_PORTAL_RETURN_POLL_ATTEMPTS: z.string().regex(/^\d+$/).optional(),

  // Features
  VITE_BULK_UPLOAD_ENABLED: z.enum(["true", "false"]).optional(),
})

const EnvSchema = BaseEnvSchema.superRefine((env, context) => {
  if (env.MODE === "production" && !isVersionPinnedViewer3DWidgetUrl(env.VITE_VIEWER3D_WIDGET_URL))
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["VITE_VIEWER3D_WIDGET_URL"],
      message:
        "Production requires an explicit HTTPS version URL such as " +
        "https://tintvto.com/3d-viewer/1.0.0/widget.js; /latest/ and other hosts are not allowed.",
    })
})

type Env = z.infer<typeof EnvSchema>

/**
 * Validates and returns typed environment variables
 * Throws an error if validation fails
 */
function getTypedEnv(): Env {
  try {
    return EnvSchema.parse(import.meta.env)
  } catch (error) {
    if (error instanceof z.ZodError) {
      const missingVars = error.errors
        .map((err) => `  - ${err.path.join(".")}: ${err.message}`)
        .join("\n")

      throw new Error(
        `❌ Invalid environment variables:\n${missingVars}\n\n` +
          `Please check your .env file and ensure all required variables are set.`
      )
    }

    throw error
  }
}

/**
 * Type-safe environment variables
 * Use this instead of import.meta.env throughout the application
 *
 * @example
 * import { typedEnv } from '@/config/typedEnv'
 *
 * const apiUrl = typedEnv.VITE_PUBLIC_API_URL
 */
export const typedEnv = getTypedEnv()

/**
 * Helper to check if running in development mode
 */
export const isDevelopment = typedEnv.MODE === "development"

/**
 * Helper to check if running in production mode
 */
export const isProduction = typedEnv.MODE === "production"

// Local-only QA affordance: ONLY the `development` mode may bypass auth.
// Deployed stands (devstand/test/production) always require a real sign-in —
// the dev deploy pipeline additionally asserts the flag is absent.
export const isLocalAdminAuthBypassEnabled =
  isDevelopment && typedEnv.VITE_LOCAL_ADMIN_AUTH_BYPASS === "true"

// Bulk CSV product upload is switched off product-wide (daily decision,
// 2026-07-23) until the importer gets per-row error handling and progress
// reporting; opt a stand back in with VITE_BULK_UPLOAD_ENABLED=true.
export const isBulkUploadEnabled = typedEnv.VITE_BULK_UPLOAD_ENABLED === "true"
